import { useTranslation } from 'react-i18next';
import { preferenceSlot } from '~/hooks/usePreferenceWriteQueue';
import { savedViewMessages } from '~/utils/savedViewMessages';
import {
  useSavedFilterViews,
  type SavedFilterView,
  type SavedFilterViews,
} from '~/hooks/useSavedFilterViews';
import type { GetUserPreferencesQuery } from '~/graphql/graphql';
import type { BuyListFilterRow } from '~/pages/buy-lists/lib/buyListFilters';
import {
  isBuyListFilterData,
  readBuyListFilterRows,
  rowsToFilterData,
} from '~/pages/buy-lists/lib/buyListFilterStorage';

const BUY_LISTS_VIEW = 'BUY_LISTS' as const;

// The buy list tables are buyer-only, so there is no selling side to scope against.
const BUY_LISTS_PERSPECTIVE = 'BUYING' as const;

type Preferences = GetUserPreferencesQuery['userPreferences'] | undefined;

export type BuyListSavedView = SavedFilterView;
export type BuyListSavedViews = SavedFilterViews<BuyListFilterRow>;

function activeIdOf(preferences: Preferences): string | null {
  return preferences?.buyerBuyListsActiveFilterId ?? null;
}

function anonymousOf(preferences: Preferences): unknown {
  return preferences?.buyListFilter;
}

/**
 * Named filter presets for the organization buy list table, over the shared
 * {@link useSavedFilterViews} lifecycle.
 *
 * @param teamActive persisted alongside the unnamed filter rows, since the team toggle
 *   is part of what the buyer is working in rather than part of a named preset.
 */
export function useBuyListSavedViews(teamActive: boolean): BuyListSavedViews {
  const { t } = useTranslation('inventory');

  return useSavedFilterViews<BuyListFilterRow>({
    view: BUY_LISTS_VIEW,
    perspective: BUY_LISTS_PERSPECTIVE,
    slot: preferenceSlot('buyLists', BUY_LISTS_PERSPECTIVE),
    isOwnFilterData: isBuyListFilterData,
    readRows: readBuyListFilterRows,
    rowsToFilterData: (rows) => rowsToFilterData(rows),
    activeIdOf,
    anonymousOf,
    anonymousPreferences: (rows) => ({
      buyListFilter: rowsToFilterData(rows, teamActive),
    }),
    preferencesSurvivingClear: () => ({
      buyListFilter: rowsToFilterData([], teamActive),
    }),
    messages: savedViewMessages(t, 'inventory.buy_list.filter'),
  });
}
